import { useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Archive, CheckCircle2, Loader2, Pencil, RotateCcw, Save, Search, X } from 'lucide-react';
import type {
  CarrierInitializationIgnoredRecord,
  CarrierRecordIgnoreReason,
} from '@/services/api';
import {
  PROPOSED_NEW_CARRIER_PREFIX,
  type CarrierMappingTableRow,
  type CarrierMappingTableRowStatus,
} from './carrierMappingDraftState';

type StatusFilter = 'all' | 'ignored' | CarrierMappingTableRowStatus;
type DirectionFilter = 'all' | 'inbound' | 'outbound';

const STATUS_FILTERS: Array<[StatusFilter, string]> = [
  ['proposed', 'Proposed'],
  ['needs_decision', 'Needs decision'],
  ['done', 'Done'],
  ['all', 'All'],
  ['ignored', 'Ignored'],
];

const RECORD_TYPE_LABELS: Record<CarrierInitializationIgnoredRecord['recordType'], string> = {
  customer_company: 'Customer company',
  customer_account: 'Inbound',
  vendor_carrier: 'Outbound',
};

interface CarrierMappingTableProps {
  rows: CarrierMappingTableRow[];
  carrierOptions: Array<{ value: string; label: string }>;
  serviceDefinitions: Array<{ id: string; name: string }>;
  confirmableCarrierCount: number;
  summary: string;
  disabled: boolean;
  saving: boolean;
  dirty: boolean;
  error: string | null;
  headerActions?: ReactNode;
  newCarrierNames: Record<string, string>;
  carrierNames: Record<string, string>;
  ignoredRecords: CarrierInitializationIgnoredRecord[];
  ignoreReasons: Array<{ value: CarrierRecordIgnoreReason; label: string }>;
  onRenameCarrier: (target: string, name: string) => void;
  // Resolves to the indexes of the given rows that were ignored.
  onIgnore: (rows: CarrierMappingTableRow[], reason: CarrierRecordIgnoreReason) => Promise<number[]>;
  notices?: ReactNode;
  onRestore: (record: CarrierInitializationIgnoredRecord) => void;
  onClose: () => void;
  onSave: () => void;
  onSetCarrier: (row: CarrierMappingTableRow, target: string) => void;
  onSetService: (row: CarrierMappingTableRow, serviceDefinitionId: string) => void;
  onAccept: (rows: CarrierMappingTableRow[]) => void;
  onConfirmComplete: () => void;
}

// Full-screen review of every open trunk (ADR-0024 section 4, reduced for
// release 1): filter, select, and accept proposals in one action. Proposed
// values show pre-selected with a dot; they join the edited mapping only when
// accepted or changed by the administrator.
export function CarrierMappingTable({
  rows,
  carrierOptions,
  serviceDefinitions,
  confirmableCarrierCount,
  summary,
  disabled,
  saving,
  dirty,
  error,
  headerActions,
  newCarrierNames,
  carrierNames,
  ignoredRecords,
  ignoreReasons,
  onRenameCarrier,
  onIgnore,
  onRestore,
  notices,
  onClose,
  onSave,
  onSetCarrier,
  onSetService,
  onAccept,
  onConfirmComplete,
}: CarrierMappingTableProps) {
  const [status, setStatus] = useState<StatusFilter>('proposed');
  const [direction, setDirection] = useState<DirectionFilter>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [renaming, setRenaming] = useState<{ rowKey: string; target: string; value: string } | null>(null);
  const [ignoring, setIgnoring] = useState<{ key: string; reason: CarrierRecordIgnoreReason | '' } | null>(null);
  const [bulkIgnoreReason, setBulkIgnoreReason] = useState<CarrierRecordIgnoreReason | '' | null>(null);

  const counts = useMemo(() => {
    const result: Record<StatusFilter, number> = {
      all: rows.length,
      proposed: 0,
      needs_decision: 0,
      done: 0,
      ignored: ignoredRecords.length,
    };
    for (const row of rows) result[row.status] += 1;
    return result;
  }, [rows, ignoredRecords.length]);
  const normalizedQuery = query.trim().toLocaleLowerCase('en-US');
  const shown = status === 'ignored' ? [] : rows.filter((row) => (
    (status === 'all' || row.status === status)
    && (direction === 'all' || row.direction === direction)
    && (!normalizedQuery
      || row.name.toLocaleLowerCase('en-US').includes(normalizedQuery)
      || (row.companyName ?? '').toLocaleLowerCase('en-US').includes(normalizedQuery))
  ));
  const selectedShown = shown.filter((row) => selected.has(row.key));
  const acceptable = selectedShown.filter((row) => row.status === 'proposed');
  const allShownSelected = shown.length > 0 && selectedShown.length === shown.length;
  const busy = disabled || saving;
  const shownIgnored = status !== 'ignored' ? [] : ignoredRecords.filter((record) => (
    !normalizedQuery || record.name.toLocaleLowerCase('en-US').includes(normalizedQuery)
  ));
  const commitRename = () => {
    if (renaming && renaming.value.trim()) onRenameCarrier(renaming.target, renaming.value.trim());
    setRenaming(null);
  };

  const toggleAll = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allShownSelected) for (const row of shown) next.delete(row.key);
      else for (const row of shown) next.add(row.key);
      return next;
    });
  };
  const toggle = (key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const accept = () => {
    onAccept(acceptable);
    // Keep selections hidden by the current filters.
    setSelected((current) => {
      const next = new Set(current);
      for (const row of acceptable) next.delete(row.key);
      return next;
    });
  };

  // Rendered at the page root so a transformed chat card cannot clip it.
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Carrier setup table" className="fixed inset-0 z-50 flex flex-col bg-surface-50 dark:bg-surface-950">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-200 bg-white px-4 py-3 dark:border-surface-800 dark:bg-surface-900">
        <div className="min-w-0">
          <p className="text-[0.65rem] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">Carrier setup</p>
          <h2 className="text-lg font-semibold text-surface-950 dark:text-white">Review all trunks</h2>
          <p className="text-xs text-surface-500 dark:text-surface-400">{summary}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {headerActions}
          <button type="button" disabled={busy || confirmableCarrierCount === 0} onClick={onConfirmComplete} title="Confirm every Carrier whose trunks all have a Carrier and a Service Type." className="flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50">
            <CheckCircle2 className="h-3.5 w-3.5" />Confirm complete Carriers ({confirmableCarrierCount})
          </button>
          <button type="button" disabled={busy || !dirty} onClick={onSave} className="flex h-8 items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Save changes
          </button>
          <button type="button" onClick={onClose} aria-label="Close table" title="Back to the chat. Unsaved changes stay until you save or reload." className="flex h-8 w-8 items-center justify-center rounded-lg text-surface-500 hover:bg-surface-100 hover:text-surface-900 dark:hover:bg-surface-800 dark:hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-surface-200 bg-white px-4 py-2 dark:border-surface-800 dark:bg-surface-900">
        <div className="flex rounded-lg bg-surface-100 p-0.5 dark:bg-surface-800">
          {STATUS_FILTERS.map(([value, label]) => (
            <button key={value} type="button" onClick={() => { setStatus(value); setSelected(new Set()); }} className={`rounded-md px-2.5 py-1 text-xs font-semibold ${status === value ? 'bg-white text-surface-950 shadow-sm dark:bg-surface-700 dark:text-white' : 'text-surface-500 dark:text-surface-400'}`}>
              {label} <span className="font-normal opacity-70">{counts[value]}</span>
            </button>
          ))}
        </div>
        <select aria-label="Direction" value={direction} onChange={(event) => setDirection(event.target.value as DirectionFilter)} className="h-8 rounded-lg border border-surface-300 bg-white px-2 text-xs dark:border-surface-700 dark:bg-surface-950">
          <option value="all">Inbound and Outbound</option>
          <option value="inbound">Inbound only</option>
          <option value="outbound">Outbound only</option>
        </select>
        <label className="relative min-w-48 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-surface-400" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search trunks or companies…" className="h-8 w-full rounded-lg border border-surface-300 bg-white pl-8 pr-2 text-xs dark:border-surface-700 dark:bg-surface-950" />
        </label>
        {bulkIgnoreReason === null ? (
          <button type="button" disabled={busy || selectedShown.length === 0 || status === 'ignored'} onClick={() => setBulkIgnoreReason('')} title="Ignore the selected trunks. They leave their Carriers; your other changes are saved first." className="flex h-8 items-center gap-1 rounded-lg border border-surface-300 px-3 text-xs font-semibold text-surface-700 hover:border-amber-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-surface-700 dark:text-surface-200">
            <Archive className="h-3.5 w-3.5" />Ignore selected ({selectedShown.length})
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <select aria-label="Ignore reason for the selected trunks" value={bulkIgnoreReason} onChange={(event) => setBulkIgnoreReason(event.target.value as CarrierRecordIgnoreReason | '')} className="h-8 rounded-lg border border-surface-300 bg-white px-2 text-xs dark:border-surface-700 dark:bg-surface-950">
              <option value="">Reason…</option>
              {ignoreReasons.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
            </select>
            <button type="button" disabled={busy || !bulkIgnoreReason || selectedShown.length === 0} onClick={() => {
              if (!bulkIgnoreReason) return;
              const rowsToIgnore = selectedShown;
              setBulkIgnoreReason(null);
              // Only rows that were ignored leave the selection, so a failure can be retried.
              void onIgnore(rowsToIgnore, bulkIgnoreReason).then((ignored) => {
                setSelected((current) => {
                  const next = new Set(current);
                  for (const index of ignored) next.delete(rowsToIgnore[index]!.key);
                  return next;
                });
              });
            }} className="h-8 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 disabled:opacity-50">Ignore {selectedShown.length}</button>
            <button type="button" onClick={() => setBulkIgnoreReason(null)} className="h-8 px-1.5 text-xs text-surface-500">Cancel</button>
          </div>
        )}
        <button type="button" disabled={busy || acceptable.length === 0} onClick={accept} className="h-8 rounded-lg border border-sky-500 bg-sky-50 px-3 text-xs font-semibold text-sky-900 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-100">
          Accept selected ({acceptable.length})
        </button>
      </div>

      {notices}
      {error && (
        <p role="alert" className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">{error}</p>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        {status !== 'ignored' && (
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-10 bg-surface-100 text-left text-[0.68rem] uppercase tracking-wide text-surface-500 dark:bg-surface-900 dark:text-surface-400">
            <tr>
              <th className="w-8 px-3 py-2"><input type="checkbox" aria-label="Select all shown trunks" checked={allShownSelected} onChange={toggleAll} /></th>
              <th className="px-2 py-2">Trunk</th>
              <th className="w-20 px-2 py-2">Direction</th>
              <th className="w-64 px-2 py-2">Carrier</th>
              <th className="w-56 px-2 py-2">Service Type</th>
              <th className="px-2 py-2">Why</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.key} className="border-b border-surface-200 align-top hover:bg-white dark:border-surface-800 dark:hover:bg-surface-900">
                <td className="px-3 py-2"><input type="checkbox" aria-label={`Select ${row.name}`} checked={selected.has(row.key)} onChange={() => toggle(row.key)} /></td>
                <td className="px-2 py-2">
                  <span className="block break-words font-medium text-surface-900 dark:text-surface-100">{row.name}</span>
                  {row.companyName && <span className="block break-words text-[0.68rem] text-surface-500 dark:text-surface-400">{row.companyName}</span>}
                </td>
                <td className="px-2 py-2 text-surface-600 dark:text-surface-300">{row.direction === 'inbound' ? 'Inbound' : 'Outbound'}</td>
                <td className="px-2 py-2">
                  {renaming && renaming.rowKey === row.key ? (
                    <input
                      autoFocus
                      aria-label="Carrier name"
                      value={renaming.value}
                      maxLength={256}
                      onChange={(event) => setRenaming({ ...renaming, value: event.target.value })}
                      onBlur={commitRename}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') commitRename();
                        if (event.key === 'Escape') setRenaming(null);
                      }}
                      className="h-8 w-full rounded-lg border border-amber-500 bg-white px-2 text-xs dark:bg-surface-950"
                    />
                  ) : (
                    <div className="flex items-center gap-1.5">
                      {row.carrierProposed && <span aria-label="Proposed" title="Proposed, not yet accepted" className="h-2 w-2 shrink-0 rounded-full bg-sky-500" />}
                      <select aria-label={`Carrier for ${row.name}`} disabled={busy} value={row.carrierTarget ?? ''} onChange={(event) => onSetCarrier(row, event.target.value)} className={`h-8 w-full rounded-lg border bg-white px-2 text-xs dark:bg-surface-950 ${row.carrierProposed ? 'border-sky-400 text-sky-900 dark:border-sky-700 dark:text-sky-100' : 'border-surface-300 dark:border-surface-700'}`}>
                        <option value="">No Carrier</option>
                        {row.carrierTarget?.startsWith(PROPOSED_NEW_CARRIER_PREFIX) && (
                          <option value={row.carrierTarget}>New: {newCarrierNames[row.carrierTarget] ?? 'Carrier'}</option>
                        )}
                        {carrierOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                        <option value="new">+ New Carrier (rename it with the pencil)</option>
                      </select>
                      {row.carrierTarget && !row.carrierTarget.startsWith(PROPOSED_NEW_CARRIER_PREFIX) && (
                        <button type="button" disabled={busy} aria-label={`Rename Carrier of ${row.name}`} title="Rename this Carrier" onClick={() => setRenaming({ rowKey: row.key, target: row.carrierTarget!, value: carrierNames[row.carrierTarget!] ?? '' })} className="shrink-0 rounded p-1 text-surface-400 hover:text-amber-700 disabled:opacity-40">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1.5">
                    {row.serviceProposed && <span aria-label="Proposed" title="Proposed, not yet accepted" className="h-2 w-2 shrink-0 rounded-full bg-sky-500" />}
                    <select aria-label={`Service Type for ${row.name}`} disabled={busy || !row.carrierTarget || serviceDefinitions.length === 0} title={!row.carrierTarget ? 'Choose a Carrier first.' : undefined} value={row.serviceDefinitionId ?? ''} onChange={(event) => onSetService(row, event.target.value)} className={`h-8 w-full rounded-lg border bg-white px-2 text-xs disabled:opacity-60 dark:bg-surface-950 ${row.serviceProposed ? 'border-sky-400 text-sky-900 dark:border-sky-700 dark:text-sky-100' : 'border-surface-300 dark:border-surface-700'}`}>
                      <option value="">Select Service Type</option>
                      {serviceDefinitions.map((definition) => (
                        <option key={definition.id} value={definition.id}>{definition.name}</option>
                      ))}
                    </select>
                  </div>
                </td>
                <td className="px-2 py-2 text-[0.7rem] leading-4 text-surface-600 dark:text-surface-300">
                  {ignoring?.key === row.key ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <select aria-label={`Ignore reason for ${row.name}`} value={ignoring.reason} onChange={(event) => setIgnoring({ key: row.key, reason: event.target.value as CarrierRecordIgnoreReason | '' })} className="h-7 rounded-lg border border-surface-300 bg-white px-1.5 text-xs dark:border-surface-700 dark:bg-surface-950">
                        <option value="">Reason…</option>
                        {ignoreReasons.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
                      </select>
                      <button type="button" disabled={busy || !ignoring.reason} onClick={() => { if (ignoring.reason) void onIgnore([row], ignoring.reason); setIgnoring(null); }} className="h-7 rounded-lg bg-amber-500 px-2 text-xs font-semibold text-surface-950 disabled:opacity-50">Ignore</button>
                      <button type="button" onClick={() => setIgnoring(null)} className="h-7 px-1.5 text-xs text-surface-500">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <span>
                        {row.carrierReason && <span className="block">{row.carrierReason}</span>}
                        {row.serviceReason && <span className="block">{row.serviceReason}</span>}
                        {!row.carrierReason && !row.serviceReason && row.status === 'needs_decision' && (
                          <span className="block text-surface-400">No evidence in the name.</span>
                        )}
                      </span>
                      <button type="button" disabled={busy} title={dirty ? 'Ignore this trunk. Your other changes are saved first.' : 'Ignore this trunk. It leaves its Carrier, and its past traffic stays.'} onClick={() => setIgnoring({ key: row.key, reason: '' })} aria-label={`Ignore ${row.name}`} className="flex h-7 shrink-0 items-center gap-1 rounded-lg border border-surface-300 px-2 text-xs text-surface-600 hover:border-amber-500 hover:text-amber-700 disabled:opacity-30 dark:border-surface-700 dark:text-surface-300">
                        <Archive className="h-3.5 w-3.5" />Ignore
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
        {status === 'ignored' && (
          <ul className="divide-y divide-surface-200 dark:divide-surface-800">
            {shownIgnored.map((record) => (
              <li key={`${record.recordType}:${record.providerRecordId}`} className="flex items-center justify-between gap-3 px-4 py-2 text-xs">
                <span className="min-w-0">
                  <span className="block break-words font-medium text-surface-900 dark:text-surface-100">{record.name}</span>
                  <span className="block text-[0.68rem] text-surface-500 dark:text-surface-400">
                    {RECORD_TYPE_LABELS[record.recordType]} · {ignoreReasons.find((reason) => reason.value === record.reason)?.label ?? record.reason} · {record.ignoredBy.username}
                    {!record.present && ' · no longer on the Switch Platform'}
                  </span>
                </span>
                <button type="button" disabled={busy || dirty} title={dirty ? 'Save changes before you restore a record.' : undefined} onClick={() => onRestore(record)} className="flex h-7 shrink-0 items-center gap-1 rounded-lg border border-surface-300 px-2 text-xs font-semibold disabled:opacity-40 dark:border-surface-700">
                  <RotateCcw className="h-3.5 w-3.5" />Restore
                </button>
              </li>
            ))}
          </ul>
        )}
        {(status === 'ignored' ? shownIgnored.length === 0 : shown.length === 0) && (
          <p className="px-4 py-8 text-center text-xs text-surface-500 dark:text-surface-400">
            {status === 'ignored' ? 'No ignored records.' : 'No trunks match this filter.'}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}
